import { haversine } from './haversine'

// Find the parameter t ∈ [0,1] of the closest point on segment [a,b] to point p.
// Uses flat-earth (Cartesian) approximation — fine for short segments (<1 km).
function closestTOnSegment(
  pLat: number, pLng: number,
  aLat: number, aLng: number,
  bLat: number, bLng: number,
): number {
  const dx = bLat - aLat
  const dy = bLng - aLng
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return 0
  const t = ((pLat - aLat) * dx + (pLng - aLng) * dy) / lenSq
  return Math.max(0, Math.min(1, t))
}

/**
 * Sum all segment lengths of a route polyline (meters).
 * Used to normalize remaining distance against the official leg distance.
 */
export function routeTotalLength(route: [number, number][]): number {
  let total = 0
  for (let i = 1; i < route.length; i++) {
    total += haversine(route[i - 1][0], route[i - 1][1], route[i][0], route[i][1])
  }
  return total
}

/**
 * Calculate the remaining route distance (meters) from a position to the end of a polyline.
 * Snaps the position to the nearest point on the route, then sums remaining segment lengths.
 * Falls back to straight-line haversine when route has fewer than 2 points.
 */
export function distanceRemainingAlongRoute(
  lat: number,
  lng: number,
  route: [number, number][],
): number {
  if (route.length < 2) {
    return haversine(lat, lng, route[route.length - 1][0], route[route.length - 1][1])
  }

  // Build cumulative distances at each vertex
  const cumDist: number[] = [0]
  for (let i = 1; i < route.length; i++) {
    cumDist.push(
      cumDist[i - 1] + haversine(route[i - 1][0], route[i - 1][1], route[i][0], route[i][1]),
    )
  }

  // Find nearest point on route
  let bestSegIdx = 0
  let bestT = 0
  let bestDistSq = Infinity

  for (let i = 0; i < route.length - 1; i++) {
    const t = closestTOnSegment(lat, lng, route[i][0], route[i][1], route[i + 1][0], route[i + 1][1])
    const closeLat = route[i][0] + t * (route[i + 1][0] - route[i][0])
    const closeLng = route[i][1] + t * (route[i + 1][1] - route[i][1])
    const dLat = lat - closeLat
    const dLng = lng - closeLng
    const distSq = dLat * dLat + dLng * dLng
    if (distSq < bestDistSq) {
      bestDistSq = distSq
      bestSegIdx = i
      bestT = t
    }
  }

  // Distance along route from snap point to end
  const segLen = cumDist[bestSegIdx + 1] - cumDist[bestSegIdx]
  const distAtSnapPoint = cumDist[bestSegIdx] + bestT * segLen
  const totalLen = cumDist[cumDist.length - 1]
  return Math.max(0, totalLen - distAtSnapPoint)
}

/**
 * Project a position forward along a polyline route.
 *
 * @param lat            Last known latitude
 * @param lng            Last known longitude
 * @param route          Array of [lat, lng] waypoints defining the route
 * @param projectedMeters Distance to project forward (meters)
 * @returns [lat, lng] of the projected position, or null if route is too short
 */
export function projectAlongRoute(
  lat: number,
  lng: number,
  route: [number, number][],
  projectedMeters: number,
): [number, number] | null {
  if (route.length < 2) return null

  // Build cumulative haversine distances (meters) at each vertex
  const cumDist: number[] = [0]
  for (let i = 1; i < route.length; i++) {
    cumDist.push(
      cumDist[i - 1] + haversine(route[i - 1][0], route[i - 1][1], route[i][0], route[i][1]),
    )
  }

  // Find the segment closest to the last known position
  let bestSegIdx = 0
  let bestT = 0
  let bestDistSq = Infinity

  for (let i = 0; i < route.length - 1; i++) {
    const t = closestTOnSegment(lat, lng, route[i][0], route[i][1], route[i + 1][0], route[i + 1][1])
    const closeLat = route[i][0] + t * (route[i + 1][0] - route[i][0])
    const closeLng = route[i][1] + t * (route[i + 1][1] - route[i][1])
    const dLat = lat - closeLat
    const dLng = lng - closeLng
    const distSq = dLat * dLat + dLng * dLng
    if (distSq < bestDistSq) {
      bestDistSq = distSq
      bestSegIdx = i
      bestT = t
    }
  }

  // Cumulative distance along the route to the snap point
  const segLen = cumDist[bestSegIdx + 1] - cumDist[bestSegIdx]
  const startDist = cumDist[bestSegIdx] + bestT * segLen

  // Target cumulative distance
  const targetDist = startDist + projectedMeters

  // Clamp to end of route
  const totalLen = cumDist[cumDist.length - 1]
  if (targetDist >= totalLen) {
    return route[route.length - 1]
  }

  // Walk forward along the polyline to the target distance
  for (let i = 0; i < route.length - 1; i++) {
    if (cumDist[i + 1] > targetDist) {
      const t = (targetDist - cumDist[i]) / (cumDist[i + 1] - cumDist[i])
      return [
        route[i][0] + t * (route[i + 1][0] - route[i][0]),
        route[i][1] + t * (route[i + 1][1] - route[i][1]),
      ]
    }
  }

  return route[route.length - 1]
}
